import { SliceComponentProps } from '@prismicio/react';
import { HorizontalContentCards } from '@/components/redesign/HorizontalContentCards';
import { HorizontalScrollableContentCards } from '@/components/redesign/HorizontalScrollableContentCards';
import { NumberedIconStepCard } from '@/components/redesign/NumberedIconStepCard';
import {
  PARAGRAPH_STEPS_GRID_CLASS,
  ParagraphStepCard,
  paragraphStepsGridStyle,
  STACKED_GRID_CLASS,
  StackedStepCard,
  type RedesignSlice,
} from './components';

export type HeadlineDescriptionCtaStepsRedesignProps =
  SliceComponentProps<RedesignSlice>;

/**
 * Assembles the shared redesign section for every redesign variation: the
 * shell (dark/light chrome, two-tone headline, optional summary and background)
 * is built once here, and each variation's cards are delegated to the component
 * that owns that layout.
 */
const HeadlineDescriptionCtaStepsRedesign = ({
  slice,
}: HeadlineDescriptionCtaStepsRedesignProps): JSX.Element => {
  const { theme, headline, subheadline, summary, background_image } =
    slice.primary;

  const shell = {
    sliceType: slice.slice_type,
    variation: slice.variation,
    theme,
    backgroundImage: background_image,
    headline,
    subheadline,
    summary,
  };

  if (slice.variation === 'redesignCardsStacked') {
    return (
      <HorizontalContentCards {...shell} gridClassName={STACKED_GRID_CLASS}>
        {slice.primary.steps.map((step, index) => (
          <StackedStepCard key={index} step={step} />
        ))}
      </HorizontalContentCards>
    );
  }

  if (slice.variation === 'redesignParagraphSteps') {
    // Read inside the branch: the other redesign variations have no card
    // background field, so this must stay out of the shared shell above.
    const cardsBackground = slice.primary.cards_background_image;
    const summaryParagraphClassName =
      theme === 'dark' ? 'text-white' : 'text-[#041118]';

    return (
      <HorizontalContentCards
        {...shell}
        gridClassName={PARAGRAPH_STEPS_GRID_CLASS}
        gridStyle={paragraphStepsGridStyle(slice.primary.steps.length)}
        headerClassName="max-w-none"
        summaryClassName="max-w-none"
        summaryParagraphClassName={summaryParagraphClassName}
        summaryStrongClassName="font-semibold"
      >
        {slice.primary.steps.map((step, index) => (
          <ParagraphStepCard
            key={index}
            step={step}
            index={index}
            backgroundImage={cardsBackground}
          />
        ))}
      </HorizontalContentCards>
    );
  }

  // Headline, subheadline and summary run the full section width in this
  // layout — the shell's default 900/720 caps left them noticeably narrower
  // than the cards row below, which is what the bug report flagged. Matches
  // how `redesignParagraphSteps` opts out.
  // The `footnote` field is unique to this variation; the shell renders it
  // below the cards, and the 36px gap falls out of the shell's `gap-9`.
  const fullAlphaParagraphClassName =
    theme === 'dark' ? 'text-white' : 'text-[#041118]';

  // `redesignCardsHorizontalScroll` shares every field, prop and card with
  // `redesignCardsHorizontal` — only the row container swaps in for one that
  // rails horizontally (peeking next card, hidden scrollbar, wheel-to-scroll).
  if (slice.variation === 'redesignCardsHorizontalScroll') {
    return (
      <HorizontalScrollableContentCards
        {...shell}
        headerClassName="max-w-none"
        summaryClassName="max-w-none"
        summaryParagraphClassName={fullAlphaParagraphClassName}
        footnote={slice.primary.footnote}
        footnoteParagraphClassName={fullAlphaParagraphClassName}
        primaryCta={slice.primary.primary_cta}
      >
        {slice.primary.steps.map((step, index) => (
          <NumberedIconStepCard key={index} step={step} index={index} />
        ))}
      </HorizontalScrollableContentCards>
    );
  }

  return (
    <HorizontalContentCards
      {...shell}
      headerClassName="max-w-none"
      summaryClassName="max-w-none"
      summaryParagraphClassName={fullAlphaParagraphClassName}
      footnote={slice.primary.footnote}
      footnoteParagraphClassName={fullAlphaParagraphClassName}
      primaryCta={slice.primary.primary_cta}
    >
      {slice.primary.steps.map((step, index) => (
        <NumberedIconStepCard
          key={index}
          step={step}
          index={index}
          footnote={step.step_footnote}
        />
      ))}
    </HorizontalContentCards>
  );
};

export default HeadlineDescriptionCtaStepsRedesign;
